/**
 * Product model
 * @class Product
 */
export class Product {
  constructor({
    id = null,
    productName = '',
    productCode = '',
    productNameVat = null,
    productCodeVat = null,
    productTradeName = null,
    productBrandCode = null,
    alias = '',
    sku = '',
    barCode = null,
    parentProductId = null,
    productCategory = null,
    productWebCategory = 0,
    originalPrice = 0,
    sellingPrice = 0,
    listedPrice = 0,
    wholesalePrice = 0,
    returnSellingPrice = 0,
    unitId = null,
    productType = 1,
    original = null,
    brand = null,
    color = null,
    size = null,
    weight = 0,
    length = 0,
    width = 0,
    height = 0,
    warrantyAddress = '',
    warrantyPhone = '',
    warrantyMonthNo = 0,
    warrantyDescription = '',
    status = 1,
    note = '',
    deleted = 0,
    productVat = null,
    optionValues = null,
    accessories = [],
    imageIds = [],
  } = {}) {
    this.id = id;
    this.productName = productName;
    this.productCode = productCode;
    this.productNameVat = productNameVat;
    this.productCodeVat = productCodeVat;
    this.productTradeName = productTradeName;
    this.productBrandCode = productBrandCode;
    this.alias = alias;
    this.sku = sku;
    this.barCode = barCode;
    this.parentProductId = parentProductId;
    this.productCategory = productCategory;
    this.productWebCategory = productWebCategory;
    this.originalPrice = originalPrice;
    this.sellingPrice = sellingPrice;
    this.listedPrice = listedPrice;
    this.wholesalePrice = wholesalePrice;
    this.returnSellingPrice = returnSellingPrice;
    this.unitId = unitId;
    this.productType = productType;
    this.original = original;
    this.brand = brand;
    this.color = color;
    this.size = size;
    this.weight = weight;
    this.length = length;
    this.width = width;
    this.height = height;
    this.warrantyAddress = warrantyAddress;
    this.warrantyPhone = warrantyPhone;
    this.warrantyMonthNo = warrantyMonthNo;
    this.warrantyDescription = warrantyDescription;
    this.status = status;
    this.note = note;
    this.deleted = deleted;
    this.productVat = productVat;
    this.optionValues = optionValues;
    this.accessories = accessories;
    this.imageIds = imageIds;
  }

  /**
   * Create from API data
   * @param {Object} data - API data
   * @returns {Product} - Product instance
   */
  static fromApiData(data) {
    return new Product(data);
  }

  /**
   * Prepare for API submission
   * @returns {Object} - API data
   */
  toApiData() {
    return {
      id: this.id,
      productName: this.productName ? this.productName.trim() : null,
      productCode: this.productCode ? this.productCode.trim() : null,
      productNameVat: this.productNameVat ? this.productNameVat.trim() : null,
      productCodeVat: this.productCodeVat ? this.productCodeVat.trim() : null,
      productTradeName: this.productTradeName
        ? this.productTradeName.trim()
        : null,
      productBrandCode: this.productBrandCode
        ? this.productBrandCode.trim()
        : null,
      alias: this.alias ? this.alias.trim() : '',
      sku: this.sku ? this.sku.trim() : '',
      barCode: this.barCode ? this.barCode.trim() : null,
      parentProductId: this.parentProductId,
      productCategory: this.productCategory,
      productWebCategory: this.productWebCategory,
      originalPrice: this.originalPrice,
      sellingPrice: this.sellingPrice,
      listedPrice: this.listedPrice,
      wholesalePrice: this.wholesalePrice,
      returnSellingPrice: this.returnSellingPrice,
      unitId: this.unitId,
      productType: this.productType,
      original: this.original,
      brand: this.brand,
      color: this.color,
      size: this.size,
      weight: this.weight,
      length: this.length,
      width: this.width,
      height: this.height,
      warrantyAddress: this.warrantyAddress,
      warrantyPhone: this.warrantyPhone,
      warrantyMonthNo: this.warrantyMonthNo,
      warrantyDescription: this.warrantyDescription,
      status: this.status,
      note: this.note,
      deleted: this.deleted,
      productVat: this.productVat,
      optionValues: this.optionValues,
      accessories: this.accessories,
      imageIds: this.imageIds,
    };
  }
}

/**
 * Child product model
 * @class ChildProduct
 * @extends Product
 */
export class ChildProduct extends Product {
  constructor(props = {}) {
    super(props);
    this.count = props.count || 0;
    this.totalQualityInstock = props.totalQualityInstock || 0;
  }

  /**
   * Create from parent product
   * @param {Product} parentProduct - Parent product
   * @param {Object} childProps - Child properties
   * @returns {ChildProduct} - Child product instance
   */
  static fromParent(parentProduct, childProps = {}) {
    const baseProps = {
      parentProductId: parentProduct.id,
      productCategory: parentProduct.productCategory,
      productWebCategory: parentProduct.productWebCategory,
      wholesalePrice: parentProduct.wholesalePrice,
      unitId: parentProduct.unitId,
      listedPrice: parentProduct.listedPrice,
      productType: parentProduct.productType,
      warrantyMonthNo: parentProduct.warrantyMonthNo,
      status: parentProduct.status,
      note: parentProduct.note,
      productVat: parentProduct.productVat,
      brand: parentProduct.brand,
    };

    return new ChildProduct({
      ...baseProps,
      ...childProps,
    });
  }
}

/**
 * Combo product model
 * @class ComboProduct
 */
export class ComboProduct {
  constructor({
    id = null,
    productCode = '',
    productName = '',
    quantity = 1,
    productComboPrice = '',
    productComboRate = '',
    isEdit = false,
  } = {}) {
    this.id = id;
    this.productCode = productCode;
    this.productName = productName;
    this.quantity = quantity;
    this.productComboPrice = productComboPrice;
    this.productComboRate = productComboRate;
    this.isEdit = isEdit;
  }

  /**
   * Create from API data
   * @param {Object} data - API data
   * @returns {ComboProduct} - ComboProduct instance
   */
  static fromApiData(data) {
    return new ComboProduct(data);
  }

  /**
   * Prepare for API submission
   * @returns {Object} - API data
   */
  toApiData() {
    return {
      id: this.id,
      productCode: this.productCode,
      productName: this.productName,
      quantity: this.quantity,
      productComboPrice: this.productComboPrice,
      productComboRate: this.productComboRate,
    };
  }
}

/**
 * Product option model
 * @class ProductOption
 */
export class ProductOption {
  constructor({ name = '', values = [] } = {}) {
    this.name = name;
    this.values = values;
  }

  /**
   * Add a value
   * @param {String} value - Option value
   */
  addValue(value) {
    if (!this.values.includes(value)) {
      this.values.push(value);
    }
  }

  /**
   * Remove a value
   * @param {String} value - Option value
   */
  removeValue(value) {
    this.values = this.values.filter((v) => v !== value);
  }

  /**
   * Create from API data
   * @param {Object} data - API data
   * @returns {ProductOption} - ProductOption instance
   */
  static fromApiData(data) {
    return new ProductOption(data);
  }

  /**
   * Prepare for API submission
   * @returns {Object} - API data
   */
  toApiData() {
    return {
      name: this.name,
      values: this.values,
    };
  }
}
